import { Link } from "react-router-dom";
import { Phone, MessageCircle, ArrowUpRight } from "lucide-react";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "@/data/site";
import { Img } from "@/components/Img";
import { Reveal } from "@/components/Reveal";

export function CtaButtons({ light = false, id = "cta", text }) {
  return (
    <div className="flex flex-wrap gap-3">
      <Link to="/booking" className="btn-primary" data-testid={`${id}-book`}>Book Your Taxi<ArrowUpRight className="h-4 w-4" aria-hidden /></Link>
      <a href={`tel:${PHONE_TEL}`} className={light ? "btn-ghost-light" : "btn-outline"} data-testid={`${id}-call`}><Phone className="h-4 w-4" aria-hidden />Call {PHONE_DISPLAY}</a>
      <a href={waLink(text)} target="_blank" rel="noopener noreferrer" className="btn-wa" data-testid={`${id}-whatsapp`}><MessageCircle className="h-4 w-4" aria-hidden />WhatsApp</a>
    </div>
  );
}

export function FinalCta({ title = "Your Rajasthan journey starts with one call.", image = "desert", id = "final-cta" }) {
  return (
    <section className="container-x py-16 md:py-24" aria-labelledby={`${id}-title`}>
      <Reveal className="grain relative isolate overflow-hidden rounded-[28px] bg-ink px-6 py-14 sm:px-12 md:py-20">
        <Img k={image} alt="" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-40" sizes="(max-width: 1320px) 100vw, 1320px" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
        <span className="eyebrow-light"><span className="h-px w-8 bg-current" />Talk to Lucky Choudhary directly</span>
        <h2 id={`${id}-title`} className="mt-4 max-w-2xl text-4xl font-semibold leading-[1.02] text-white sm:text-5xl lg:text-6xl">{title}</h2>
        <p className="mt-5 max-w-xl text-base text-zinc-300 md:text-lg">Share your pickup, drop and date. We confirm the vehicle and the current fare before you travel — no surprises.</p>
        <div className="mt-8"><CtaButtons light id={id} /></div>
      </Reveal>
    </section>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-body">
        {items.map((it, i) => (
          <li key={it.path} className="flex items-center gap-1.5">
            {i > 0 && <span aria-hidden className="text-gold-deep">/</span>}
            {i === items.length - 1 ? <span aria-current="page" className="font-semibold text-ink">{it.label}</span> : <Link to={it.path} className="hover:text-saffron">{it.label}</Link>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PageHero({ crumbs, eyebrow, title, subtitle, image, children }) {
  return (
    <header className="jaali relative overflow-hidden border-b border-line bg-white">
      <div className="container-x grid items-center gap-8 py-7 md:py-9 lg:grid-cols-12 lg:py-10">
        <div className="lg:col-span-8">
          <Breadcrumbs items={crumbs} />
          {eyebrow && <span className="eyebrow mt-5"><span className="h-px w-8 bg-gold" />{eyebrow}</span>}
          <h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-[1.02] sm:text-5xl lg:text-[56px]" data-testid="page-h1"><span className="line-mask"><span>{title}</span></span></h1>
          <span className="mt-4 block h-1 w-20 rounded-full bg-gold" aria-hidden />
          {subtitle && <p className="mt-4 max-w-2xl text-base leading-relaxed text-body md:text-lg" data-testid="page-subtitle">{subtitle}</p>}
          {children}
        </div>
        <div className="hidden lg:col-span-4 lg:block">
          <div className="relative mx-auto max-w-md lg:max-w-none">
            <div className="absolute -right-3 -top-3 h-full w-full rounded-[28px] border-2 border-gold/60" aria-hidden />
            <div className="relative overflow-hidden rounded-[28px] arch">
              <Img k={image} priority className="aspect-[4/3] w-full object-cover" sizes="30vw" />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export function LinkCard({ item, index, testid }) {
  return (
    <Link to={item.path} className="group flex items-center justify-between gap-3 rounded-xl border border-line bg-white px-4 py-3.5 text-[15px] font-semibold text-ink transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-saffron hover:shadow-[0_14px_30px_-20px_rgba(29,78,158,0.6)]" data-testid={testid}>
      <span className="flex items-center gap-3">{index !== undefined && <span className="font-display text-lg italic text-saffron">{String(index + 1).padStart(2, "0")}</span>}{item.label}</span>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-saffron transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden />
    </Link>
  );
}
